import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { PAGE_SIZE, RailTimeline } from '../common';
import type { ActivityFeedEvent } from '@bbbk/schema';
import { dayHeading, timeOfDay, pageViewRow, actionText, sectionLabel } from './activityFormat';

// The selected user's activity in the edit-user modal rail — the EXACT same
// component the booking modal uses (RailTimeline), banded by day with one
// continuous spine per day. Loads the 20 most-recent events first, then lazily
// fetches the next 20 as you scroll the rail (infinite scroll), appending — so
// the rail is a glance that deepens on demand rather than one capped page.
export function UserActivityRail({ email }: { email: string }) {
  const [events, setEvents] = useState<ActivityFeedEvent[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errored, setErrored] = useState(false);
  const busy = useRef(false);

  const load = useCallback(
    async (offset: number) => {
      if (busy.current) return;
      busy.current = true;
      if (offset > 0) setLoadingMore(true);
      try {
        const params = new URLSearchParams({ window: 'month', limit: String(PAGE_SIZE), offset: String(offset), actor: email });
        const res = await api<ActivityFeedEvent[]>(`/activity/feed?${params.toString()}`);
        const rows = res.data ?? [];
        setEvents((prev) => (offset === 0 ? rows : [...(prev ?? []), ...rows]));
        if (res.page?.total != null) setTotal(res.page.total);
      } catch {
        if (offset === 0) setEvents([]);
        setErrored(true);
      } finally {
        busy.current = false;
        setLoadingMore(false);
      }
    },
    [email],
  );

  // (Re)load from the top whenever the selected user changes.
  useEffect(() => {
    setEvents(null);
    setTotal(null);
    setErrored(false);
    void load(0);
  }, [email, load]);

  const hasMore = events != null && total != null && events.length < total;
  const onReachEnd = useCallback(() => {
    if (hasMore && !busy.current) void load(events!.length);
  }, [hasMore, load, events]);

  // The same node shape as the booking timeline (.node › .spine ▸ .dot, .lab ›
  // .txt ▸ .rng + .sb), so the spine, dots and rows render identically.
  // The same phrasing the everyone-feed ledger uses, so the two surfaces can't
  // word one event differently. Two rules carried across from it:
  //  - Dwell rides on the TIME line, never inline behind a middle dot: a page's
  //    own name can contain one ("My Shifts · Today"), so an inline "· 40s" made
  //    one glyph join section-to-tab AND label-to-dwell, and the row scanned as
  //    two page views (Freddi, 2026-07-26).
  //  - Actions read as English rather than as the wire label.
  const renderNode = (e: ActivityFeedEvent): ReactNode => {
    const view = e.kind === 'page_view' ? pageViewRow(e) : null;
    return (
      <div key={e.id} className={'node' + (e.kind === 'login' ? ' login' : '')}>
        <div className="spine"><div className="dot" /></div>
        <div className="lab">
          <div className="txt">
            <div className="rng">
              {timeOfDay(e.at)}
              {view?.dwell && <span className="rail-dwell"> · {view.dwell}</span>}
            </div>
            <div className="sb">
              {e.kind === 'login' ? (
                // A login carries the page it landed on, so the rail names it
                // too — otherwise drilling into a person LOST the detail the
                // ledger shows (Codex, PR #149), which is exactly what sharing
                // these helpers is meant to prevent.
                <>signed in{e.label ? ` on ${sectionLabel(e.label)}` : ''}</>
              ) : view ? (
                view.text
              ) : (
                <>
                  {actionText(e.label)}
                  {e.target ? ` → ${e.target}` : ''}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <RailTimeline<ActivityFeedEvent>
      headLabel="Activity"
      items={events ?? []}
      dateOf={(e) => e.at}
      groupOf={(e) => dayHeading(e.at)}
      renderNode={renderNode}
      loading={events === null}
      loadingMore={loadingMore}
      onReachEnd={onReachEnd}
      emptyText={errored ? 'Couldn’t load activity.' : 'No activity in the last 30 days.'}
    />
  );
}
